import { useState } from 'react'
import { HandelsregisterTab } from './HandelsregisterTab.tsx'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Clock, Edit, ExternalLink, FileText, ListTodo, Linkedin, MapPin, Play, Plus, RefreshCw, Search, Sparkles, StickyNote, Terminal, Trash2, UserCheck, Wand2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  syncCustomerToNinja,
  getNinjaSyncLog,
  getNinjaPolicies,
  getNinjaRoles,
  getCustomerOrgPolicies,
  setCustomerOrgPolicies,
  refreshNinjaPoliciesCache,
  listNinjaScripts,
  refreshNinjaScriptsCache,
  getCustomerDevices,
  runNinjaScript,
  runNinjaScriptBulk,
  listNinjaScriptRuns,
  listCustomerLocations,
  createCustomerLocation,
  updateCustomerLocation,
  deleteCustomerLocation,
  syncLocationToNinja,
  type NinjaSyncLogEntry,
  type NinjaPolicy,
  type NinjaRole,
  type OrgPolicyMapping,
  type NinjaScript,
  type NinjaDevice,
  type NinjaScriptRunResult,
  type CustomerLocation,
} from '@/api/ninjaAdapter.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import type { CachedDocument } from '@/api/sapDocuments.ts'
import { DOC_TYPE_LABELS } from '@/api/sapDocuments.ts'
import type { ColumnDef } from '@tanstack/react-table'

// ─── Typen ────────────────────────────────────────────────────────────────────

interface QualityIssue {
  code: string
  message: string
  severity: 'error' | 'warning'
}

interface BpAddress {
  street?: string
  zipCode?: string
  city?: string
  country?: string
  [key: string]: unknown
}

interface BpAddressRow {
  id: string | null
  addressName: string | null
  addressType: string | null
  street: string | null
  zipCode: string | null
  city: string | null
  country: string | null
  state: string | null
  isDefault: number | boolean
}

interface BpContactRow {
  sourceId: string | null
  firstName: string | null
  lastName: string | null
  title: string | null
  position: string | null
  department: string | null
  phone: string | null
  mobilePhone: string | null
  email: string | null
  isDefault: number | boolean
  isActive: number | boolean
}

interface BusinessPartner {
  id: string
  name: string
  bpType: 'customer' | 'supplier' | 'prospect'
  isActive: boolean
  sapCardCode: string | null
  sapCardType: string | null
  tanssId: string | null
  tanssCustomerType: string | null
  esetCustomerId: string | null
  alsoCustomerId: string | null
  cwaClientId: string | null
  m365TenantId: string | null
  ninjaoneOrgId: string | null
  address: BpAddress | null
  contacts: unknown
  tags: string[] | null
  industrySapCode: string | null
  industryName: string | null
  website: string | null
  city: string | null
  country: string | null
  zipCode: string | null
  street: string | null
  contactPhone: string | null
  syncedAt: string | null
  createdAt: string
  updatedAt: string
  qualityScore: number
  qualityIssues: QualityIssue[]
  sapRawJson: Record<string, unknown> | null
  bpAddresses: BpAddressRow[]
  bpContacts: BpContactRow[]
}

interface NormalizePreview {
  address: {
    before: { street?: string | null; zipCode?: string | null; city?: string | null; country?: string | null }
    after: { street: string | null; zipCode: string | null; city: string | null; country: string | null }
    changed: boolean
  }
  phone: { before: string | null; after: string | null; valid: boolean; error?: string } | null
  vatId: {
    before: string | null
    after: string | null
    formatValid: boolean
    vies?: { valid: boolean; name?: string; error?: string }
  } | null
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  customer: 'Kunde',
  supplier: 'Lieferant',
  prospect: 'Prospect',
}
const TYPE_STATUS: Record<string, string> = {
  customer: 'active',
  supplier: 'info',
  prospect: 'pending',
}

function qualityVariant(score: number): string {
  if (score >= 75) return 'success'
  if (score >= 50) return 'warning'
  return 'danger'
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-small font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-body text-gray-900">{value || '—'}</dd>
    </div>
  )
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
      {children}
    </dl>
  )
}

// ─── Quality Block ────────────────────────────────────────────────────────────

function QualityBlock({ score, issues }: { score: number; issues: QualityIssue[] }) {
  if (score === 100) {
    return (
      <Card>
        <Card.Body>
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-body font-medium">Datenqualität vollständig</span>
            <StatusBadge status="success" label="100 %" />
          </div>
        </Card.Body>
      </Card>
    )
  }
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          <h3 className="text-h4 text-gray-900">Datenqualität</h3>
          <StatusBadge status={qualityVariant(score)} label={`${score} %`} />
        </div>
      </Card.Header>
      <Card.Body>
        <ul className="space-y-1">
          {issues.map((issue) => (
            <li key={issue.code} className="flex items-start gap-2 text-small">
              <AlertCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${issue.severity === 'error' ? 'text-red-500' : 'text-orange-400'}`} />
              <span className="text-gray-700">{issue.message}</span>
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  )
}

// ─── Stammdaten-Tab ───────────────────────────────────────────────────────────

function AddressEditRow({ bp, addr, onRefresh }: { bp: BusinessPartner; addr: BpAddressRow; onRefresh: () => void }) {
  const { toast } = useToast()
  const [editingAddr, setEditingAddr] = useState(false)
  const [form, setForm] = useState({
    street: addr.street ?? '',
    zipCode: addr.zipCode ?? '',
    city: addr.city ?? '',
    country: addr.country ?? '',
  })

  const saveMutation = useMutation({
    mutationFn: () => apiClient.patch(`/business-partners/${bp.id}/addresses/${addr.id}`, {
      street: form.street || null,
      zipCode: form.zipCode || null,
      city: form.city || null,
      country: form.country || null,
    }),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Adresse gespeichert', description: 'Wird ggf. an SAP übertragen.' })
      setEditingAddr(false)
      onRefresh()
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  return (
    <div className="flex items-start gap-3 p-3 rounded border border-gray-100 bg-gray-50/50">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          {addr.addressName && <span className="text-small font-medium text-gray-700">{addr.addressName}</span>}
          <StatusBadge
            status={addr.addressType === 'billing' ? 'success' : addr.addressType === 'shipping' ? 'info' : 'neutral'}
            label={addr.addressType === 'billing' ? 'Rechnung' : addr.addressType === 'shipping' ? 'Lieferung' : 'Sonstige'}
          />
          {(addr.isDefault === 1 || addr.isDefault === true) && (
            <StatusBadge status="accent" label="Standard" />
          )}
        </div>
        {editingAddr ? (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="col-span-2">
              <label className="text-small font-medium text-gray-500 block mb-0.5">Straße</label>
              <Input value={form.street} onChange={(e) => setForm((p) => ({ ...p, street: e.target.value }))} />
            </div>
            <div>
              <label className="text-small font-medium text-gray-500 block mb-0.5">PLZ</label>
              <Input value={form.zipCode} onChange={(e) => setForm((p) => ({ ...p, zipCode: e.target.value }))} />
            </div>
            <div>
              <label className="text-small font-medium text-gray-500 block mb-0.5">Stadt</label>
              <Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
            </div>
            <div>
              <label className="text-small font-medium text-gray-500 block mb-0.5">Land</label>
              <Input value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="primary" size="xs" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>Speichern</Button>
              <Button variant="ghost" size="xs" onClick={() => { setEditingAddr(false); setForm({ street: addr.street ?? '', zipCode: addr.zipCode ?? '', city: addr.city ?? '', country: addr.country ?? '' }) }}>Abbrechen</Button>
            </div>
          </div>
        ) : (
          <div className="text-small text-gray-700">
            {addr.street && <div>{addr.street}</div>}
            {(addr.zipCode || addr.city) && <div>{[addr.zipCode, addr.city].filter(Boolean).join(' ')}</div>}
            {addr.country && <div>{addr.country}</div>}
          </div>
        )}
      </div>
      {!editingAddr && addr.id && (
        <Button variant="ghost" size="xs" onClick={() => setEditingAddr(true)}>
          <Edit className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  )
}

// ─── NinjaOne Richtlinien Card ────────────────────────────────────────────────

const NO_POLICY_SENTINEL = '__none__'

function NinjaPoliciesCard({ bp }: { bp: BusinessPartner }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const policiesQuery = useQuery<NinjaPolicy[]>({
    queryKey: ['ninja-policies'],
    queryFn: () => getNinjaPolicies().then(r => r.data),
  })

  const rolesQuery = useQuery<NinjaRole[]>({
    queryKey: ['ninja-roles'],
    queryFn: () => getNinjaRoles().then(r => r.data),
  })

  const orgPoliciesQuery = useQuery<OrgPolicyMapping[]>({
    queryKey: ['ninja-org-policies', bp.id],
    queryFn: () => getCustomerOrgPolicies(bp.id).then(r => r.data),
    enabled: !!bp.ninjaoneOrgId,
  })

  // Local state: roleId → policyId (as string for Select, or NO_POLICY_SENTINEL)
  const [draft, setDraft] = useState<Record<number, string>>({})
  const [initialized, setInitialized] = useState(false)

  // Sync live mappings into draft whenever they load
  const orgPolicies = orgPoliciesQuery.data
  if (orgPolicies && !initialized) {
    const initial: Record<number, string> = {}
    for (const m of orgPolicies) {
      initial[m.nodeRoleId] = String(m.policyId)
    }
    setDraft(initial)
    setInitialized(true)
  }

  const refreshMutation = useMutation({
    mutationFn: () => refreshNinjaPoliciesCache(),
    onSuccess: (res) => {
      toast({ tone: 'success', title: 'Richtlinien aktualisiert', description: `${res.data.count} Richtlinien geladen` })
      void queryClient.invalidateQueries({ queryKey: ['ninja-policies'] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler beim Refresh', description: err.message }),
  })

  const saveMutation = useMutation({
    mutationFn: () => {
      const mappings: OrgPolicyMapping[] = Object.entries(draft)
        .filter(([, policyId]) => policyId !== NO_POLICY_SENTINEL)
        .map(([roleId, policyId]) => ({ nodeRoleId: Number(roleId), policyId: Number(policyId) }))
      return setCustomerOrgPolicies(bp.id, mappings)
    },
    onSuccess: () => {
      toast({ tone: 'success', title: 'Richtlinien gespeichert', description: 'Zuweisung wurde an NinjaOne übertragen.' })
      void queryClient.invalidateQueries({ queryKey: ['ninja-org-policies', bp.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler beim Speichern', description: err.message }),
  })

  const isLoading = policiesQuery.isLoading || rolesQuery.isLoading || orgPoliciesQuery.isLoading
  const error = policiesQuery.error ?? rolesQuery.error ?? orgPoliciesQuery.error

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <h3 className="text-h4 text-gray-900">NinjaOne Richtlinien</h3>
          {bp.ninjaoneOrgId && <StatusBadge status="info" label={`Org ${bp.ninjaoneOrgId}`} />}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending || isLoading}
            title="Richtlinienliste aus NinjaOne neu laden"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="primary"
            size="xs"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || isLoading || !initialized}
          >
            Speichern
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {isLoading && <LoadingState label="Lade Richtlinien …" />}

        {error && (
          <ErrorState
            title="Fehler beim Laden"
            description={(error as Error)?.message ?? 'Unbekannter Fehler'}
          />
        )}

        {!isLoading && !error && rolesQuery.data && (
          <div className="space-y-3">
            {rolesQuery.data.length === 0 && (
              <p className="text-small text-gray-400">Keine Geräterollen in NinjaOne gefunden.</p>
            )}
            {rolesQuery.data.map(role => {
              const currentValue = draft[role.id] ?? NO_POLICY_SENTINEL
              return (
                <div key={role.id} className="flex items-center gap-3">
                  <span className="w-40 text-small font-medium text-gray-700 shrink-0">{role.name}</span>
                  <div className="flex-1">
                    <SelectRoot
                      value={currentValue}
                      onValueChange={(v) => setDraft(prev => ({ ...prev, [role.id]: v }))}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Keine Richtlinie" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_POLICY_SENTINEL}>— Keine Richtlinie —</SelectItem>
                        {(policiesQuery.data ?? []).map(policy => (
                          <SelectItem key={policy.id} value={String(policy.id)}>
                            {policy.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectRoot>
                  </div>
                </div>
              )
            })}

            {policiesQuery.data?.length === 0 && (
              <p className="text-small text-gray-400 mt-2">
                Keine Richtlinien in NinjaOne gefunden. Klicke auf „Refresh" um die Liste neu zu laden.
              </p>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function StammdatenTab({ bp, onRefresh }: { bp: BusinessPartner; onRefresh: () => void }) {
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)
  const [sapRaw, setSapRaw] = useState<unknown>(null)
  const [sapRawLoading, setSapRawLoading] = useState(false)
  const addr = bp.address as BpAddress | null
  const [form, setForm] = useState({
    name: bp.name ?? '',
    street: bp.street ?? addr?.street ?? '',
    zipCode: bp.zipCode ?? addr?.zipCode ?? '',
    city: bp.city ?? addr?.city ?? '',
    country: bp.country ?? addr?.country ?? '',
    contactPhone: bp.contactPhone ?? '',
    website: bp.website ?? '',
    industryName: bp.industryName ?? '',
  })

  const saveMutation = useMutation({
    mutationFn: () => apiClient.patch(`/business-partners/${bp.id}`, {
      name: form.name || null,
      street: form.street || null,
      zipCode: form.zipCode || null,
      city: form.city || null,
      country: form.country || null,
      contactPhone: form.contactPhone || null,
      website: form.website || null,
      industryName: form.industryName || null,
    }),
    onSuccess: (res) => {
      const data = res.data as { queued?: string[] }
      const queued = data?.queued ?? []
      toast({
        tone: 'success',
        title: 'Gespeichert',
        description: queued.length > 0
          ? `${queued.join(', ')} werden an SAP übertragen.`
          : 'Änderungen wurden gespeichert.',
      })
      setEditing(false)
      onRefresh()
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const F = ({ label, field }: { label: string; field: keyof typeof form }) => (
    editing ? (
      <div>
        <label className="text-small font-medium text-gray-500 block mb-0.5">{label}</label>
        <Input
          value={form[field]}
          onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
        />
      </div>
    ) : (
      <Field label={label} value={form[field] || null} />
    )
  )

  return (
    <div className="space-y-4">
      <QualityBlock score={bp.qualityScore ?? 0} issues={bp.qualityIssues ?? []} />

      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Identifikation</h3>
          {!editing && (
            <Button variant="ghost" size="xs" onClick={() => setEditing(true)}>
              <Edit className="w-3.5 h-3.5 mr-1" />
              Bearbeiten
            </Button>
          )}
          {editing && (
            <div className="flex gap-2">
              <Button variant="primary" size="xs" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                Speichern
              </Button>
              <Button variant="ghost" size="xs" onClick={() => setEditing(false)}>
                Abbrechen
              </Button>
            </div>
          )}
        </Card.Header>
        <Card.Body>
          <FieldGrid>
            <Field label="Typ" value={TYPE_LABEL[bp.bpType]} />
            <Field label="SAP CardCode" value={bp.sapCardCode} />
            <Field label="SAP CardType" value={bp.sapCardType} />
            {bp.sapRawJson?.['RegistrationNumber'] ? (
              <Field label="Handelsreg.-Nr. (SAP)" value={String(bp.sapRawJson['RegistrationNumber'])} />
            ) : null}
            <Field label="TANSS-ID" value={bp.tanssId} />
            <Field label="ESET-Kunden-ID" value={bp.esetCustomerId} />
            <Field label="ALSO-Kunden-ID" value={bp.alsoCustomerId} />
            <Field label="NinjaOne Org-ID" value={bp.ninjaoneOrgId} />
          </FieldGrid>
        </Card.Body>
      </Card>

      {bp.bpType === 'customer' && (
        <NinjaOneSyncCard bp={bp} onRefresh={onRefresh} />
      )}

      {bp.bpType === 'customer' && bp.ninjaoneOrgId && (
        <NinjaPoliciesCard bp={bp} />
      )}

      {/* Mehrere Adressen aus bp_addresses, Fallback auf flache Spalten */}
      {bp.bpAddresses && bp.bpAddresses.length > 0 ? (
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <h3 className="text-h4 text-gray-900">Adressen</h3>
              <StatusBadge status="neutral" label={`${bp.bpAddresses.length}`} />
            </div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-3">
              {bp.bpAddresses.map((a, i) => (
                <AddressEditRow key={a.id ?? i} bp={bp} addr={a} onRefresh={onRefresh} />
              ))}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h3 className="text-h4 text-gray-900">Adresse</h3>
            {editing && <StatusBadge status="info" label="→ SAP-Queue" />}
          </Card.Header>
          <Card.Body>
            <FieldGrid>
              <F label="Straße" field="street" />
              <F label="PLZ" field="zipCode" />
              <F label="Stadt" field="city" />
              <F label="Land" field="country" />
            </FieldGrid>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Branche & Web</h3>
          {editing && <StatusBadge status="info" label="→ SAP-Queue" />}
        </Card.Header>
        <Card.Body>
          <FieldGrid>
            <Field label="Branchencode (SAP)" value={bp.industrySapCode} />
            <F label="Branchenname" field="industryName" />
            <F label="Website" field="website" />
          </FieldGrid>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Systemdaten</h3>
        </Card.Header>
        <Card.Body>
          <FieldGrid>
            <Field label="Angelegt" value={bp.createdAt ? new Date(bp.createdAt).toLocaleString('de-DE') : null} />
            <Field label="Zuletzt geändert" value={bp.updatedAt ? new Date(bp.updatedAt).toLocaleString('de-DE') : null} />
            <Field label="Letzter SAP-Sync" value={bp.syncedAt ? new Date(bp.syncedAt).toLocaleString('de-DE') : null} />
          </FieldGrid>
        </Card.Body>
      </Card>
      {bp.sapCardCode && (
        <Card>
          <Card.Header>
            <h3 className="text-h4 text-gray-900">SAP-Rohdaten (Debug)</h3>
            <Button
              variant="ghost"
              size="xs"
              onClick={async () => {
                setSapRawLoading(true)
                try {
                  const r = await apiClient.get(`/business-partners/${bp.id}/sap-raw`)
                  setSapRaw(r.data)
                } catch (err) {
                  setSapRaw({ error: (err as Error).message })
                } finally {
                  setSapRawLoading(false)
                }
              }}
              disabled={sapRawLoading}
            >
              {sapRawLoading ? 'Lade …' : 'Aus SAP laden'}
            </Button>
          </Card.Header>
          {sapRaw !== null && (
            <Card.Body>
              <details open>
                <summary className="text-small text-gray-500 cursor-pointer mb-2">
                  BPAddresses ({((sapRaw as { sapExpanded?: { BPAddresses?: unknown[] } })?.sapExpanded?.BPAddresses?.length ?? 0)} Einträge)
                </summary>
                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify((sapRaw as { sapExpanded?: { BPAddresses?: unknown } })?.sapExpanded?.BPAddresses ?? null, null, 2)}
                </pre>
              </details>
              <details className="mt-2">
                <summary className="text-small text-gray-500 cursor-pointer mb-2">Kompletter SAP-Response</summary>
                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-96 whitespace-pre-wrap">
                  {JSON.stringify(sapRaw, null, 2)}
                </pre>
              </details>
            </Card.Body>
          )}
        </Card>
      )}

      {bp.bpType === 'customer' && (
        <NinjaOneSyncCard bp={bp} onRefresh={onRefresh} />
      )}

      {bp.bpType === 'customer' && bp.ninjaoneOrgId && (
        <NinjaLocationsCard customerId={bp.id} />
      )}

      {bp.bpType === 'customer' && bp.ninjaoneOrgId && (
        <NinjaScriptsCard bp={bp} />
      )}
    </div>
  )
}

// ─── NinjaOne Locations Card ─────────────────────────────────────────────────

function NinjaLocationsCard({ customerId }: { customerId: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAddress, setNewAddress] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editAddress, setEditAddress] = useState('')

  const { data: locations, isLoading, error } = useQuery<CustomerLocation[]>({
    queryKey: ['ninja-locations', customerId],
    queryFn: () => listCustomerLocations(customerId).then(r => r.data),
    retry: false,
  })

  const createMutation = useMutation({
    mutationFn: () => createCustomerLocation(customerId, newName.trim(), newAddress.trim() || undefined),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Standort erstellt' })
      setShowAddForm(false)
      setNewName('')
      setNewAddress('')
      void queryClient.invalidateQueries({ queryKey: ['ninja-locations', customerId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      updateCustomerLocation(customerId, id, editName.trim(), editAddress.trim() || undefined),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Standort aktualisiert' })
      setEditingId(null)
      void queryClient.invalidateQueries({ queryKey: ['ninja-locations', customerId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const deleteMutation = useMutation({
    mutationFn: (locationId: string) => deleteCustomerLocation(customerId, locationId),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Standort gelöscht' })
      void queryClient.invalidateQueries({ queryKey: ['ninja-locations', customerId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const syncMutation = useMutation({
    mutationFn: (locationId: string) => syncLocationToNinja(customerId, locationId),
    onSuccess: (res) => {
      const data = res.data
      toast({
        tone: 'success',
        title: data.action === 'create' ? 'Standort in NinjaOne angelegt' : 'Standort in NinjaOne aktualisiert',
        description: `NinjaOne Location-ID: ${data.ninjaLocationId}`,
      })
      void queryClient.invalidateQueries({ queryKey: ['ninja-locations', customerId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'NinjaOne-Sync fehlgeschlagen', description: err.message }),
  })

  function startEdit(loc: CustomerLocation) {
    setEditingId(loc.id)
    setEditName(loc.name)
    setEditAddress(loc.address ?? '')
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-500" />
          <h3 className="text-h4 text-gray-900">NinjaOne Standorte</h3>
          {locations && locations.length > 0 && (
            <span className="text-small text-gray-400">({locations.length})</span>
          )}
        </div>
        <Button variant="secondary" size="xs" onClick={() => setShowAddForm(v => !v)}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Standort hinzufügen
        </Button>
      </Card.Header>

      <Card.Body className="space-y-3">
        {showAddForm && (
          <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
            <Input
              label="Name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="z.B. Hauptsitz München"
            />
            <Input
              label="Adresse (optional)"
              value={newAddress}
              onChange={e => setNewAddress(e.target.value)}
              placeholder="Musterstraße 1, 80331 München"
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="xs"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !newName.trim()}
              >
                Hinzufügen
              </Button>
              <Button variant="ghost" size="xs" onClick={() => { setShowAddForm(false); setNewName(''); setNewAddress('') }}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {isLoading && <p className="text-small text-gray-400">Lade Standorte …</p>}
        {error && (
          <ErrorState
            title="Fehler beim Laden der Standorte"
            description={(error as Error).message}
          />
        )}

        {!isLoading && !error && locations && locations.length === 0 && !showAddForm && (
          <EmptyState
            title="Keine Standorte"
            description="Füge Standorte hinzu, um sie mit NinjaOne zu synchronisieren."
          />
        )}

        {locations && locations.map(loc => (
          <div key={loc.id} className="border rounded-lg p-3 space-y-2">
            {editingId === loc.id ? (
              <div className="space-y-2">
                <Input
                  label="Name"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                />
                <Input
                  label="Adresse (optional)"
                  value={editAddress}
                  onChange={e => setEditAddress(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={() => updateMutation.mutate({ id: loc.id })}
                    disabled={updateMutation.isPending || !editName.trim()}
                  >
                    Speichern
                  </Button>
                  <Button variant="ghost" size="xs" onClick={() => setEditingId(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{loc.name}</span>
                    {loc.ninjaoneLocationId && (
                      <StatusBadge status="success" label={`Location ${loc.ninjaoneLocationId}`} />
                    )}
                  </div>
                  {loc.address && (
                    <p className="text-small text-gray-500 mt-0.5">{loc.address}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => syncMutation.mutate(loc.id)}
                    disabled={syncMutation.isPending}
                    title={loc.ninjaoneLocationId ? 'In NinjaOne aktualisieren' : 'In NinjaOne anlegen'}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    {loc.ninjaoneLocationId ? 'Sync' : 'NinjaOne'}
                  </Button>
                  <IconButton
                    icon={<Edit className="w-3.5 h-3.5" />}
                    aria-label="Bearbeiten"
                    size="xs"
                    variant="ghost"
                    onClick={() => startEdit(loc)}
                  />
                  <IconButton
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    aria-label="Löschen"
                    size="xs"
                    variant="ghost"
                    onClick={() => deleteMutation.mutate(loc.id)}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}

// ─── NinjaOne Scripts Card ────────────────────────────────────────────────────

const NO_DEVICE_SENTINEL = '__none__'

function NinjaScriptsCard({ bp }: { bp: BusinessPartner }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [selectedDevice, setSelectedDevice] = useState<Record<number, string>>({})

  const scriptsQuery = useQuery<NinjaScript[]>({
    queryKey: ['ninja-scripts'],
    queryFn: () => listNinjaScripts().then(r => r.data),
    retry: false,
  })

  const devicesQuery = useQuery<NinjaDevice[]>({
    queryKey: ['ninja-devices', bp.id],
    queryFn: () => getCustomerDevices(bp.id).then(r => r.data),
    enabled: !!bp.ninjaoneOrgId,
    retry: false,
  })

  const runsQuery = useQuery<NinjaScriptRunResult[]>({
    queryKey: ['ninja-script-runs', bp.id],
    queryFn: () => listNinjaScriptRuns(bp.id).then(r => r.data),
    enabled: !!bp.ninjaoneOrgId,
    retry: false,
  })

  const refreshMutation = useMutation({
    mutationFn: () => refreshNinjaScriptsCache(),
    onSuccess: (res) => {
      toast({ tone: 'success', title: 'Skripte aktualisiert', description: `${res.data.count} Skripte geladen` })
      void queryClient.invalidateQueries({ queryKey: ['ninja-scripts'] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler beim Refresh', description: err.message }),
  })

  const runMutation = useMutation({
    mutationFn: ({ scriptId, scriptName, deviceId, deviceName }: {
      scriptId: number; scriptName: string; deviceId: number; deviceName: string
    }) => runNinjaScript(bp.id, deviceId, deviceName, scriptId, scriptName),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Skript gestartet', description: 'Ausführung wurde an NinjaOne übertragen.' })
      void queryClient.invalidateQueries({ queryKey: ['ninja-script-runs', bp.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Skript-Fehler', description: err.message }),
  })

  const bulkMutation = useMutation({
    mutationFn: ({ scriptId, scriptName }: { scriptId: number; scriptName: string }) => {
      const deviceIds = (devicesQuery.data ?? []).map(d => d.id)
      return runNinjaScriptBulk(bp.id, deviceIds, scriptId, scriptName)
    },
    onSuccess: () => {
      toast({ tone: 'success', title: 'Bulk-Ausführung gestartet', description: 'Skript auf allen Geräten gestartet.' })
      void queryClient.invalidateQueries({ queryKey: ['ninja-script-runs', bp.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Bulk-Fehler', description: err.message }),
  })

  const devices = devicesQuery.data ?? []
  const isRunning = runMutation.isPending || bulkMutation.isPending

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-gray-500" />
          <h3 className="text-h4 text-gray-900">NinjaOne Skripte</h3>
          {scriptsQuery.data && <span className="text-small text-gray-400">({scriptsQuery.data.length})</span>}
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending || scriptsQuery.isLoading}
          title="Skriptliste aus NinjaOne neu laden"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </Card.Header>
      <Card.Body className="space-y-4">
        {devicesQuery.error && (
          <ErrorState
            title="Geräte konnten nicht geladen werden"
            description={(devicesQuery.error as Error).message}
          />
        )}

        {scriptsQuery.isLoading && <LoadingState label="Lade Skripte …" />}
        {scriptsQuery.error && (
          <ErrorState
            title="Skripte konnten nicht geladen werden"
            description={(scriptsQuery.error as Error).message}
          />
        )}

        {!scriptsQuery.isLoading && !scriptsQuery.error && scriptsQuery.data && (
          <>
            {scriptsQuery.data.length === 0 && (
              <EmptyState
                title="Keine Skripte verfügbar"
                description='Klicke auf "Refresh" um Skripte aus NinjaOne zu laden.'
              />
            )}
            <div className="space-y-2">
              {scriptsQuery.data.map(script => {
                const deviceValue = selectedDevice[script.id] ?? NO_DEVICE_SENTINEL
                const selectedDev = devices.find(d => String(d.id) === deviceValue)
                return (
                  <div key={script.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 text-sm">{script.name}</span>
                        {script.language && (
                          <span className="ml-2 text-xs text-gray-400">{script.language}</span>
                        )}
                        {script.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{script.description}</p>
                        )}
                      </div>
                      <StatusBadge status="neutral" label={`ID ${script.id}`} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="w-52">
                        <SelectRoot
                          value={deviceValue}
                          onValueChange={v => setSelectedDevice(prev => ({ ...prev, [script.id]: v }))}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Gerät wählen …" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_DEVICE_SENTINEL}>— Gerät wählen —</SelectItem>
                            {devices.map(d => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.displayName || d.systemName || String(d.id)}
                                {d.online === false ? ' (offline)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </SelectRoot>
                      </div>
                      <Button
                        variant="primary"
                        size="xs"
                        disabled={deviceValue === NO_DEVICE_SENTINEL || isRunning || devices.length === 0}
                        onClick={() => {
                          if (!selectedDev) return
                          runMutation.mutate({
                            scriptId: script.id,
                            scriptName: script.name,
                            deviceId: selectedDev.id,
                            deviceName: selectedDev.displayName || selectedDev.systemName || String(selectedDev.id),
                          })
                        }}
                      >
                        <Play className="w-3.5 h-3.5 mr-1" />
                        Ausführen
                      </Button>
                      <Button
                        variant="secondary"
                        size="xs"
                        disabled={isRunning || devices.length === 0}
                        onClick={() => bulkMutation.mutate({ scriptId: script.id, scriptName: script.name })}
                        title={`Auf allen ${devices.length} Geräten ausführen`}
                      >
                        Alle Geräte ({devicesQuery.isLoading ? '…' : devices.length})
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {runsQuery.data && runsQuery.data.length > 0 && (
          <div>
            <h4 className="text-small font-medium text-gray-700 mb-2">Ausführungsverlauf</h4>
            <div className="space-y-1">
              {runsQuery.data.slice(0, 10).map(run => (
                <div key={run.id} className="flex items-center gap-2 text-xs border rounded p-2 flex-wrap">
                  {run.status === 'PENDING' && <Clock className="w-3.5 h-3.5 text-yellow-500 shrink-0" />}
                  {run.status === 'FAILED' && <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                  {run.status !== 'PENDING' && run.status !== 'FAILED' && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                  )}
                  <span className="text-gray-500 shrink-0">
                    {new Date(run.createdAt).toLocaleString('de-DE')}
                  </span>
                  <span className="font-medium text-gray-800 truncate">
                    {run.scriptName ?? `Skript ${run.scriptId}`}
                  </span>
                  {run.isBulk
                    ? <StatusBadge status="info" label="Bulk" />
                    : <span className="text-gray-500 truncate">{run.deviceName ?? `Gerät ${run.deviceId}`}</span>
                  }
                  {run.ninjaRunId && (
                    <span className="text-gray-400 font-mono truncate">#{run.ninjaRunId}</span>
                  )}
                  {run.errorMessage && (
                    <span className="text-red-600 truncate ml-auto">{run.errorMessage}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── NinjaOne Sync Card ───────────────────────────────────────────────────────

function NinjaOneSyncCard({ bp, onRefresh }: { bp: BusinessPartner; onRefresh: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const syncLog = useQuery<NinjaSyncLogEntry[]>({
    queryKey: ['ninja-sync-log', bp.id],
    queryFn: () => getNinjaSyncLog(bp.id).then(r => r.data),
    retry: false,
  })

  const syncMutation = useMutation({
    mutationFn: () => syncCustomerToNinja(bp.id),
    onSuccess: (res) => {
      const data = res.data
      toast({
        tone: 'success',
        title: data.action === 'create' ? 'NinjaOne-Organisation erstellt' : 'NinjaOne-Organisation aktualisiert',
        description: `Org-ID: ${data.ninjaOrgId}`,
      })
      onRefresh()
      void queryClient.invalidateQueries({ queryKey: ['ninja-sync-log', bp.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'NinjaOne-Sync fehlgeschlagen', description: err.message }),
  })

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <h3 className="text-h4 text-gray-900">NinjaOne</h3>
          {bp.ninjaoneOrgId && <StatusBadge status="success" label={`Org ${bp.ninjaoneOrgId}`} />}
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" />
          {bp.ninjaoneOrgId ? 'Sync aktualisieren' : 'In NinjaOne anlegen'}
        </Button>
      </Card.Header>
      <Card.Body>
        {syncLog.data && syncLog.data.length > 0 ? (
          <div className="space-y-1">
            {syncLog.data.map(entry => (
              <div key={entry.id} className="flex items-center gap-2 text-small text-gray-600">
                <StatusBadge status={entry.status === 'success' ? 'success' : 'danger'} label={entry.status} />
                <span>{entry.action}</span>
                <span className="text-gray-400">via {entry.triggeredBy}</span>
                {entry.ninjaoneOrgId ? ` • Org-ID ${entry.ninjaoneOrgId}` : ''}
                <span className="ml-auto text-gray-400">{new Date(entry.createdAt).toLocaleString('de-DE')}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-small text-gray-400">
            {syncLog.isLoading ? 'Lade …' : 'Noch kein Sync-Eintrag.'}
          </p>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Normalisierungs-Preview Modal ────────────────────────────────────────────

function NormalizeModal({
  open,
  preview,
  onClose,
  onApply,
  applying,
}: {
  open: boolean
  preview: NormalizePreview | null
  onClose: () => void
  onApply: () => void
  applying: boolean
}) {
  if (!preview) return null
  const hasChanges = preview.address.changed

  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }} title="Normalisierungs-Vorschau">
      <div className="space-y-4">
        {/* Adresse */}
        <div>
          <h4 className="text-body font-medium text-gray-900 mb-2">Adresse</h4>
          {preview.address.changed ? (
            <div className="grid grid-cols-2 gap-3 text-small">
              <div className="bg-red-50 rounded p-2">
                <div className="font-medium text-red-700 mb-1">Vorher</div>
                <div>{preview.address.before.street || '—'}</div>
                <div>{[preview.address.before.zipCode, preview.address.before.city].filter(Boolean).join(' ') || '—'}</div>
                <div>{preview.address.before.country || '—'}</div>
              </div>
              <div className="bg-green-50 rounded p-2">
                <div className="font-medium text-green-700 mb-1">Nachher</div>
                <div>{preview.address.after.street || '—'}</div>
                <div>{[preview.address.after.zipCode, preview.address.after.city].filter(Boolean).join(' ') || '—'}</div>
                <div>{preview.address.after.country || '—'}</div>
              </div>
            </div>
          ) : (
            <p className="text-small text-gray-500">Keine Adress-Änderungen notwendig.</p>
          )}
        </div>

        {/* Telefon */}
        {preview.phone && (
          <div>
            <h4 className="text-body font-medium text-gray-900 mb-2">Telefon</h4>
            <div className="flex items-center gap-2 text-small">
              <span className="text-gray-500">{preview.phone.before}</span>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
              <span className={preview.phone.valid ? 'text-green-700 font-medium' : 'text-orange-600'}>
                {preview.phone.after ?? '—'}
              </span>
              {preview.phone.error && (
                <span className="text-orange-500 text-xs">({preview.phone.error})</span>
              )}
            </div>
          </div>
        )}

        {/* USt-ID */}
        {preview.vatId && (
          <div>
            <h4 className="text-body font-medium text-gray-900 mb-2">USt-ID</h4>
            <div className="flex items-center gap-2 text-small">
              <span className="text-gray-500">{preview.vatId.before}</span>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
              <span className={preview.vatId.formatValid ? 'text-green-700 font-medium' : 'text-red-600'}>
                {preview.vatId.after ?? '—'}
              </span>
              <StatusBadge
                status={preview.vatId.formatValid ? 'success' : 'danger'}
                label={preview.vatId.formatValid ? 'Format gültig' : 'Format ungültig'}
              />
            </div>
            {preview.vatId.vies && (
              <div className="mt-1 text-small">
                {preview.vatId.vies.valid ? (
                  <span className="text-green-700">✓ VIES bestätigt{preview.vatId.vies.name ? `: ${preview.vatId.vies.name}` : ''}</span>
                ) : (
                  <span className="text-red-600">✗ VIES: {preview.vatId.vies.error ?? 'nicht bestätigt'}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button
          variant="primary"
          onClick={onApply}
          disabled={!hasChanges || applying}
        >
          <Wand2 className="w-4 h-4 mr-2" />
          {applying ? 'Wird übernommen …' : 'Änderungen übernehmen'}
        </Button>
      </div>
    </Modal>
  )
}

// ─── Enrichment-Modal ─────────────────────────────────────────────────────────

type EnrichableField = 'street' | 'zipCode' | 'city' | 'country' | 'contactPhone' | 'website' | 'industryName'
type EnrichmentSource = 'impressum' | 'offeneregister' | 'gelbeseiten' | 'apollo' | 'implisense'
type EnrichmentConfidence = 'high' | 'medium' | 'low'

interface EnrichmentSuggestion {
  field: EnrichableField
  currentValue: string | null
  suggestedValue: string
  source: EnrichmentSource
  confidence: EnrichmentConfidence
}

interface ContactSuggestion {
  firstName: string
  lastName: string
  role: string
  email?: string | null
  source: EnrichmentSource
  confidence: EnrichmentConfidence
}

interface EnrichmentResult {
  suggestions: EnrichmentSuggestion[]
  contactSuggestions: ContactSuggestion[]
  errors: { adapter: string; error: string }[]
}

const FIELD_LABEL: Record<EnrichableField, string> = {
  street: 'Straße',
  zipCode: 'PLZ',
  city: 'Stadt',
  country: 'Land',
  contactPhone: 'Telefon',
  website: 'Website',
  industryName: 'Branche',
}

const SOURCE_LABEL: Record<EnrichmentSource, string> = {
  impressum: 'Impressum',
  offeneregister: 'Handelsregister',
  gelbeseiten: 'Gelbe Seiten',
  apollo: 'Apollo.io',
  implisense: 'Implisense',
}

const CONFIDENCE_VARIANT: Record<EnrichmentConfidence, string> = {
  high: 'success',
  medium: 'warning',
  low: 'neutral',
}

function EnrichmentModal({
  open,
  result,
  onClose,
  onApply,
  applying,
}: {
  open: boolean
  result: EnrichmentResult | null
  onClose: () => void
  onApply: (selected: Record<string, string>) => void
  applying: boolean
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  // Alle "high"-Suggestions vorauswaehlen wenn Modal oeffnet
  const suggestions = result?.suggestions ?? []
  const contacts = result?.contactSuggestions ?? []

  function toggle(key: string) {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function handleApply() {
    const fields: Record<string, string> = {}
    for (const sg of suggestions) {
      if (selected[sg.field] ?? sg.confidence === 'high') {
        fields[sg.field] = sg.suggestedValue
      }
    }
    onApply(fields)
  }

  const hasSelectable = suggestions.length > 0
  const selectedCount = suggestions.filter(
    (sg) => selected[sg.field] ?? sg.confidence === 'high'
  ).length

  if (!result) return null

  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }} title="KI-Datenanreicherung">
      <div className="space-y-5">
        {suggestions.length === 0 && contacts.length === 0 ? (
          <p className="text-small text-gray-500">Keine neuen Informationen gefunden.</p>
        ) : null}

        {/* Felder */}
        {suggestions.length > 0 && (
          <div>
            <h4 className="text-body font-medium text-gray-900 mb-2">Stammdaten-Vorschläge</h4>
            <div className="space-y-2">
              {suggestions.map((sg) => {
                const isChecked = selected[sg.field] ?? sg.confidence === 'high'
                return (
                  <label key={sg.field} className="flex items-start gap-3 p-2 rounded border border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                      checked={isChecked}
                      onChange={() => toggle(sg.field)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-small font-medium text-gray-700">{FIELD_LABEL[sg.field]}</span>
                        <StatusBadge status={CONFIDENCE_VARIANT[sg.confidence]} label={SOURCE_LABEL[sg.source]} />
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-small">
                        {sg.currentValue && (
                          <span className="text-gray-400 line-through">{sg.currentValue}</span>
                        )}
                        <span className="text-green-700 font-medium">{sg.suggestedValue}</span>
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {/* Kontaktpersonen (nur anzeigen, nicht direkt uebernehmen) */}
        {contacts.length > 0 && (
          <div>
            <h4 className="text-body font-medium text-gray-900 mb-1">Gefundene Ansprechpartner</h4>
            <p className="text-xs text-gray-500 mb-2">Zur Information — bitte manuell in SAP/TANSS anlegen.</p>
            <div className="space-y-1.5">
              {contacts.map((cs, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-blue-50 text-small">
                  <div className="flex-1">
                    <span className="font-medium">{cs.firstName} {cs.lastName}</span>
                    {cs.role && <span className="text-gray-500"> · {cs.role}</span>}
                    {cs.email && <span className="text-gray-600"> · {cs.email}</span>}
                  </div>
                  <StatusBadge status={CONFIDENCE_VARIANT[cs.confidence]} label={SOURCE_LABEL[cs.source]} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Adapter-Fehler (aufklappbar) */}
        {result.errors.length > 0 && (
          <details className="text-xs text-gray-400">
            <summary className="cursor-pointer">{result.errors.length} Adapter-Fehler</summary>
            <ul className="mt-1 space-y-0.5 ml-2">
              {result.errors.map((e, i) => (
                <li key={i}><span className="font-medium">{e.adapter}:</span> {e.error}</li>
              ))}
            </ul>
          </details>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
           
            onClick={handleApply}
            disabled={!hasSelectable || selectedCount === 0 || applying}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {applying ? 'Wird übernommen …' : `${selectedCount} Feld${selectedCount !== 1 ? 'er' : ''} übernehmen`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Dubletten-Block ─────────────────────────────────────────────────────────

interface DuplicateCandidate {
  candidateId: string
  candidateName: string
  score: number
  reasons: string[]
}

function DuplicatesBlock({ bpId }: { bpId: string }) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery<DuplicateCandidate[]>({
    queryKey: ['bp-duplicates', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/duplicates`).then((r) => r.data),
  })

  if (isLoading) return <LoadingState label="Prüfe auf Dubletten …" />
  if (isError) return (
    <Card>
      <Card.Body>
        <ErrorState title="Fehler beim Dubletten-Check" description="Bitte Seite neu laden." />
      </Card.Body>
    </Card>
  )

  if (!data?.length) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <h3 className="text-h4 text-gray-900">Dubletten</h3>
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-small text-gray-500">Keine potentiellen Dubletten gefunden.</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          <h3 className="text-h4 text-gray-900">Potentielle Dubletten</h3>
          <StatusBadge status="warning" label={`${data.length} Kandidat${data.length !== 1 ? 'en' : ''}`} />
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          {data.map((c) => (
            <div key={c.candidateId} className="flex items-start justify-between gap-4 p-3 bg-orange-50 rounded-lg border border-orange-100">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-body font-medium text-gray-900">{c.candidateName}</span>
                  <StatusBadge status={c.score >= 80 ? 'danger' : 'warning'} label={`Score ${c.score}`} />
                </div>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {c.reasons.map((r) => (
                    <li key={r} className="text-xs text-orange-700 bg-orange-100 rounded px-1.5 py-0.5">{r}</li>
                  ))}
                </ul>
              </div>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => navigate(`/gp/${c.candidateId}`)}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Öffnen
              </Button>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── TANSS-Match-Block ────────────────────────────────────────────────────────

interface TanssMatchSuggestion {
  id: string
  tanssCompanyId: string
  score: number
  decision: string
  createdAt: string
}

function TanssMatchBlock({ bp }: { bp: BusinessPartner }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: suggestions, isLoading } = useQuery<TanssMatchSuggestion[]>({
    queryKey: ['bp-tanss-suggestions', bp.id],
    queryFn: () => apiClient.get(`/business-partners/${bp.id}/tanss-match-suggestions`).then((r) => r.data),
  })

  const proposeMutation = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${bp.id}/tanss-match-suggestions`, {}).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bp-tanss-suggestions', bp.id] }),
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const confirmMutation = useMutation({
    mutationFn: (sid: string) =>
      apiClient.post(`/business-partners/tanss-match-suggestions/${sid}/confirm`, {}).then((r) => r.data),
    onSuccess: () => {
      toast({ tone: 'success', title: 'TANSS-Zuordnung bestätigt' })
      queryClient.invalidateQueries({ queryKey: ['bp-tanss-suggestions', bp.id] })
      queryClient.invalidateQueries({ queryKey: ['business-partner', bp.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const rejectMutation = useMutation({
    mutationFn: (sid: string) =>
      apiClient.post(`/business-partners/tanss-match-suggestions/${sid}/reject`, {}).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bp-tanss-suggestions', bp.id] }),
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  // Bereits zugeordnet
  if (bp.tanssId) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <h3 className="text-h4 text-gray-900">TANSS-Zuordnung</h3>
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-small text-gray-600">TANSS-ID: <span className="font-medium">{bp.tanssId}</span></p>
        </Card.Body>
      </Card>
    )
  }

  const pending = suggestions?.filter((s) => s.decision === 'pending') ?? []

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-h4 text-gray-900">TANSS-Zuordnung</h3>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => proposeMutation.mutate()}
            disabled={proposeMutation.isPending}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${proposeMutation.isPending ? 'animate-spin' : ''}`} />
            Vorschläge aktualisieren
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <LoadingState label="Lade Vorschläge …" />
        ) : pending.length === 0 ? (
          <p className="text-small text-gray-500">Keine offenen Zuordnungs-Vorschläge. Klicke „Vorschläge aktualisieren".</p>
        ) : (
          <div className="space-y-2">
            {pending.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <span className="text-body font-medium text-gray-900">TANSS-ID {s.tanssCompanyId}</span>
                  <StatusBadge status={s.score >= 90 ? 'success' : 'warning'} label={`Ähnlichkeit ${s.score} %`} className="ml-2" />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={() => confirmMutation.mutate(s.id)}
                    disabled={confirmMutation.isPending}
                  >
                    Bestätigen
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => rejectMutation.mutate(s.id)}
                    disabled={rejectMutation.isPending}
                  >
                    Ablehnen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Datenpflege-Tab ──────────────────────────────────────────────────────────

function DatepflegeTab({ bp, onRefresh }: { bp: BusinessPartner; onRefresh: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [normalizeOpen, setNormalizeOpen] = useState(false)
  const [preview, setPreview] = useState<NormalizePreview | null>(null)
  const [enrichOpen, setEnrichOpen] = useState(false)
  const [enrichResult, setEnrichResult] = useState<EnrichmentResult | null>(null)

  const enrichMutation = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${bp.id}/enrich`, {}).then((r) => r.data as EnrichmentResult),
    onSuccess: (data) => {
      setEnrichResult(data)
      setEnrichOpen(true)
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Anreicherung fehlgeschlagen', description: err.message }),
  })

  const enrichApplyMutation = useMutation({
    mutationFn: (fields: Record<string, string>) =>
      apiClient.post(`/business-partners/${bp.id}/enrich/apply`, { fields }).then((r) => r.data),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Daten übernommen' })
      setEnrichOpen(false)
      queryClient.invalidateQueries({ queryKey: ['business-partner', bp.id] })
      onRefresh()
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler beim Übernehmen', description: err.message }),
  })

  const convertMutation = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${bp.id}/convert-to-customer`, {}).then((r) => r.data),
    onSuccess: (data: { cardCode: string }) => {
      toast({ tone: 'success', title: 'Prospect zu Kunde konvertiert', description: `SAP CardCode: ${data.cardCode}` })
      queryClient.invalidateQueries({ queryKey: ['business-partner', bp.id] })
      queryClient.invalidateQueries({ queryKey: ['business-partners'] })
      onRefresh()
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Konvertierung fehlgeschlagen', description: err.message }),
  })

  const previewMutation = useMutation({
    mutationFn: (checkVies: boolean) =>
      apiClient.post(`/business-partners/${bp.id}/normalize`, { checkVies, apply: false })
        .then((r) => r.data.preview as NormalizePreview),
    onSuccess: (data) => {
      setPreview(data)
      setNormalizeOpen(true)
    },
    onError: (err: Error) => {
      toast({ tone: 'danger', title: 'Fehler', description: err.message })
    },
  })

  const applyMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/business-partners/${bp.id}/normalize`, { apply: true })
        .then((r) => r.data),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Normalisierung übernommen' })
      setNormalizeOpen(false)
      queryClient.invalidateQueries({ queryKey: ['business-partner', bp.id] })
    },
    onError: (err: Error) => {
      toast({ tone: 'danger', title: 'Fehler', description: err.message })
    },
  })

  return (
    <div className="space-y-4">
      {/* KI-Anreicherung */}
      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">KI-Datenanreicherung</h3>
        </Card.Header>
        <Card.Body>
          <p className="text-small text-gray-600 mb-4">
            Fehlende Adresse, Telefon, Website und Branche automatisch aus öffentlichen
            Quellen ergänzen (Impressum, Handelsregister, Gelbe Seiten, Apollo, Implisense).
          </p>
          <Button
            variant="secondary"
           
            onClick={() => enrichMutation.mutate()}
            disabled={enrichMutation.isPending}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {enrichMutation.isPending ? 'Wird gesucht …' : 'Daten anreichern'}
          </Button>
        </Card.Body>
      </Card>

      {/* Normalisierung */}
      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Daten-Normalisierung</h3>
        </Card.Header>
        <Card.Body>
          <p className="text-small text-gray-600 mb-4">
            Adresse (PLZ-Format, Land → ISO-2), Telefon und USt-ID Format werden geprüft und bereinigt.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
             
              onClick={() => previewMutation.mutate(false)}
              disabled={previewMutation.isPending}
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {previewMutation.isPending ? 'Wird analysiert …' : 'Vorschau'}
            </Button>
            <Button
              variant="ghost"
             
              onClick={() => previewMutation.mutate(true)}
              disabled={previewMutation.isPending}
            >
              Vorschau + VIES prüfen
            </Button>
          </div>
        </Card.Body>
      </Card>

      {bp.bpType === 'prospect' && (
        <Card>
          <Card.Header>
            <h3 className="text-h4 text-gray-900">Prospect konvertieren</h3>
          </Card.Header>
          <Card.Body>
            <p className="text-small text-gray-600 mb-4">
              Legt diesen Prospect als Kunden in SAP an (falls noch kein SAP-CardCode vorhanden)
              und setzt den Typ auf „Kunde".
            </p>
            <Button
              variant="primary"
             
              onClick={() => convertMutation.mutate()}
              disabled={convertMutation.isPending}
            >
              <UserCheck className="w-4 h-4 mr-2" />
              {convertMutation.isPending ? 'Wird konvertiert …' : 'Zu Kunde konvertieren'}
            </Button>
          </Card.Body>
        </Card>
      )}

      <DuplicatesBlock bpId={bp.id} />

      <TanssMatchBlock bp={bp} />

      <NormalizeModal
        open={normalizeOpen}
        preview={preview}
        onClose={() => setNormalizeOpen(false)}
        onApply={() => applyMutation.mutate()}
        applying={applyMutation.isPending}
      />

      <EnrichmentModal
        open={enrichOpen}
        result={enrichResult}
        onClose={() => setEnrichOpen(false)}
        onApply={(fields) => enrichApplyMutation.mutate(fields)}
        applying={enrichApplyMutation.isPending}
      />
    </div>
  )
}

// ─── Placeholder-Tabs ─────────────────────────────────────────────────────────

// ─── Dokumente-Tab ────────────────────────────────────────────────────────────

const DOC_TYPE_LABEL: Record<string, string> = {
  ...DOC_TYPE_LABELS,
  'local-draft': 'Entwurf',
}

const DOC_TYPE_FILTERS = [
  { value: 'quotation', label: 'Angebote' },
  { value: 'order', label: 'Aufträge' },
  { value: 'invoice', label: 'Rechnungen' },
  { value: 'delivery', label: 'Lieferungen' },
  { value: 'credit', label: 'Gutschriften' },
]

const DOC_COLUMNS: ColumnDef<CachedDocument>[] = [
  {
    accessorKey: 'docType',
    header: 'Typ',
    cell: ({ row }) => (
      <StatusBadge
        status={row.original.docType === 'invoice' ? 'info' : row.original.docType === 'order' ? 'active' : 'default'}
        label={DOC_TYPE_LABEL[row.original.docType] ?? row.original.docType}
      />
    ),
  },
  {
    accessorKey: 'sapDocNum',
    header: 'Nummer',
    cell: ({ row }) => row.original.sapDocNum ?? row.original.sapDocEntry ?? '—',
  },
  {
    accessorKey: 'docDate',
    header: 'Datum',
    cell: ({ row }) => row.original.docDate
      ? new Date(row.original.docDate).toLocaleDateString('de-DE')
      : '—',
  },
  {
    accessorKey: 'docDueDate',
    header: 'Fälligkeit',
    cell: ({ row }) => row.original.docDueDate
      ? new Date(row.original.docDueDate).toLocaleDateString('de-DE')
      : '—',
  },
  {
    accessorKey: 'docTotal',
    header: 'Betrag',
    cell: ({ row }) =>
      row.original.docTotal != null
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: row.original.docCurrency ?? 'EUR' }).format(row.original.docTotal)
        : '—',
  },
  {
    accessorKey: 'docStatus',
    header: 'Status',
    cell: ({ row }) => {
      const s = row.original.docStatus
      if (!s) return '—'
      if (s === 'bost_Open' || s === 'open') return <StatusBadge status="active" label="Offen" />
      if (s === 'bost_Close' || s === 'closed') return <StatusBadge status="inactive" label="Geschlossen" />
      if (s === 'local-draft') return <StatusBadge status="pending" label="Entwurf" />
      return <StatusBadge status="default" label={s} />
    },
  },
]

function DokumenteTab({ bpId }: { bpId: string }) {
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [showClosed, setShowClosed] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<CachedDocument | null>(null)

  const { data, isLoading, isError, error } = useQuery<{ data: CachedDocument[]; total: number }>({
    queryKey: ['bp-documents', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/documents`).then((r) => r.data),
  })

  const filtered = (data?.data ?? []).filter((d) => {
    const isClosed = d.docStatus === 'bost_Close' || d.docStatus === 'closed' || d.isClosed
    if (!showClosed && isClosed) return false
    if (typeFilter && d.docType !== typeFilter) return false
    return true
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <FilterBar>
          {DOC_TYPE_FILTERS.map((f) => (
            <FilterChip
              key={f.value}
              active={typeFilter === f.value}
              onClick={() => setTypeFilter(typeFilter === f.value ? null : f.value)}
              onRemove={typeFilter === f.value ? () => setTypeFilter(null) : undefined}
            >
              {f.label}
            </FilterChip>
          ))}
        </FilterBar>
        <Switch
          label="Geschlossene anzeigen"
          checked={showClosed}
          onChange={(e) => setShowClosed(e.target.checked)}
        />
      </div>

      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <h3 className="text-h4 text-gray-900">SAP-Belege</h3>
            {data && <span className="text-small text-gray-500">({filtered.length} von {data.total})</span>}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading && <LoadingState label="Lade Belege …" />}
          {isError && (
            <ErrorState
              title="Fehler beim Laden"
              description={
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (error as any)?.response?.data?.error
                ?? (error as Error)?.message
                ?? 'Unbekannter Fehler'
              }
              details={
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (error as any)?.response?.data?.stack ?? null
              }
            />
          )}
          {!isLoading && !isError && (
            <DataTable
              columns={DOC_COLUMNS}
              data={filtered}
              pagination={{ pageSize: 20 }}
              onRowClick={setSelectedDoc}
              emptyState={
                <EmptyState
                  title="Keine Belege gefunden"
                  description={
                    !showClosed
                      ? 'Keine offenen SAP-Belege vorhanden. Geschlossene Belege können über den Schalter oben eingeblendet werden.'
                      : 'Für diesen Geschäftspartner sind keine SAP-Belege im Cache vorhanden.'
                  }
                />
              }
            />
          )}
        </Card.Body>
      </Card>

      <DocumentDetailModal
        doc={selectedDoc}
        open={!!selectedDoc}
        onOpenChange={(open) => { if (!open) setSelectedDoc(null) }}
        onConvert={() => {}}
      />
    </div>
  )
}

// ─── Vertraege-Tab ─────────────────────────────────────────────────────────────

interface BpContract {
  id: string
  contractNumber: string
  name: string | null
  direction: 'outbound' | 'inbound'
  status: string
  billingCycle: string
  startDate: string
  endDate: string | null
  autoRenew: boolean
  noticePeriodDays: number
  noticePeriodUnit: string
}

const CONTRACT_STATUS_MAP: Record<string, string> = {
  draft: 'pending',
  active: 'active',
  paused: 'warning',
  terminated: 'inactive',
  expired: 'inactive',
}

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  paused: 'Pausiert',
  terminated: 'Gekündigt',
  expired: 'Abgelaufen',
}

const BILLING_CYCLE_LABEL: Record<string, string> = {
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  yearly: 'Jährlich',
  once: 'Einmalig',
}

const CONTRACT_COLS: ColumnDef<BpContract>[] = [
  {
    accessorKey: 'contractNumber',
    header: 'Nummer',
    cell: ({ row }) => (
      <span className="font-mono text-small">{row.original.contractNumber}</span>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Bezeichnung',
    cell: ({ row }) => row.original.name ?? '—',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusBadge
        status={CONTRACT_STATUS_MAP[row.original.status] ?? 'default'}
        label={CONTRACT_STATUS_LABEL[row.original.status] ?? row.original.status}
      />
    ),
  },
  {
    accessorKey: 'billingCycle',
    header: 'Abrechnung',
    cell: ({ row }) => BILLING_CYCLE_LABEL[row.original.billingCycle] ?? row.original.billingCycle,
  },
  {
    accessorKey: 'startDate',
    header: 'Laufzeit',
    cell: ({ row }) => {
      const start = new Date(row.original.startDate).toLocaleDateString('de-DE')
      const end = row.original.endDate
        ? new Date(row.original.endDate).toLocaleDateString('de-DE')
        : row.original.autoRenew ? '∞ (Auto-Renewal)' : 'offen'
      return `${start} – ${end}`
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="xs"
        onClick={() => window.open(`/vertraege/${row.original.id}`, '_blank')}
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </Button>
    ),
  },
]

function VertraegeTab({ bpId }: { bpId: string }) {
  const { data, isLoading, isError, error } = useQuery<{ data: BpContract[]; total: number }>({
    queryKey: ['bp-contracts', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/contracts`).then((r) => r.data),
  })

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Lokale Verträge</h3>
          {data && <span className="text-small text-gray-500">({data.total} gesamt)</span>}
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading && <LoadingState label="Lade Verträge …" />}
          {isError && (
            <ErrorState
              title="Fehler beim Laden"
              description={(error as Error)?.message ?? 'Unbekannter Fehler'}
            />
          )}
          {!isLoading && !isError && (
            <DataTable
              columns={CONTRACT_COLS}
              data={data?.data ?? []}
              pagination={{ pageSize: 20 }}
              emptyState={
                <EmptyState
                  title="Keine Verträge"
                  description="Für diesen Geschäftspartner sind keine lokalen Verträge vorhanden."
                />
              }
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Ansprechpartner-Tab ──────────────────────────────────────────────────────

interface BpContact {
  id: string
  firstName: string | null
  lastName: string | null
  name: string
  role: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  linkedinUrl: string | null
  source: 'sap' | 'tanss' | 'local'
}

const BP_SOURCE_LABEL: Record<string, string> = { sap: 'SAP', tanss: 'TANSS', local: 'Lokal' }
const BP_SOURCE_STATUS: Record<string, string> = { sap: 'info', tanss: 'active', local: 'default' }

function AnsprechpartnerTab({ bpId }: { bpId: string }) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading, isError, error } = useQuery<{ data: BpContact[]; total: number; tanssErrors: string[] }>({
    queryKey: ['bp-contacts', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/contacts`).then((r) => r.data),
  })

  const linkedinMutation = useMutation({
    mutationFn: ({ contactId, linkedinUrl }: { contactId: string; linkedinUrl: string | null }) =>
      apiClient.patch(`/business-partners/${bpId}/contacts/${contactId}/linkedin`, { linkedinUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bp-contacts', bpId] })
      setEditingId(null)
      toast({ title: 'LinkedIn-URL gespeichert', tone: 'success' })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (err as Error)?.message ?? 'Unbekannter Fehler'
      toast({ title: 'Fehler beim Speichern', description: msg, tone: 'danger' })
    },
  })

  const columns: ColumnDef<BpContact>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <span className="font-medium text-gray-900">{row.original.name}</span>
          {row.original.role && (
            <div className="text-small text-gray-500">{row.original.role}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'E-Mail',
      cell: ({ row }) => row.original.email
        ? <a href={`mailto:${row.original.email}`} className="text-brand-600 hover:underline">{row.original.email}</a>
        : '—',
    },
    {
      accessorKey: 'phone',
      header: 'Telefon',
      cell: ({ row }) => row.original.phone ?? '—',
    },
    {
      accessorKey: 'mobile',
      header: 'Mobil',
      cell: ({ row }) => row.original.mobile ?? '—',
    },
    {
      id: 'linkedin',
      header: 'LinkedIn',
      cell: ({ row }) => {
        const c = row.original
        if (editingId === c.id) {
          return (
            <div className="flex items-center gap-2 min-w-[320px]">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="https://linkedin.com/in/..."
                className="flex-1"
              />
              <Button
                size="xs"
                variant="primary"
                onClick={() => linkedinMutation.mutate({ contactId: c.id, linkedinUrl: editValue.trim() || null })}
                disabled={linkedinMutation.isPending}
              >
                Speichern
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setEditingId(null)}>
                Abbruch
              </Button>
            </div>
          )
        }
        const linkedinSearchUrl = `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(c.name)}&origin=GLOBAL_SEARCH_HEADER`
        return (
          <div className="flex items-center gap-1.5">
            {c.linkedinUrl && (
              <a
                href={c.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0A66C2] hover:text-[#004182]"
                title="LinkedIn-Profil"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="w-4 h-4" />
              </a>
            )}
            <a href={linkedinSearchUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              <IconButton icon={<Search className="w-3.5 h-3.5" />} aria-label="Auf LinkedIn suchen" size="xs" variant="ghost" />
            </a>
            <IconButton
              icon={<Edit className="w-3.5 h-3.5" />}
              aria-label="LinkedIn-URL bearbeiten"
              size="xs"
              variant="ghost"
              onClick={() => {
                setEditingId(c.id)
                setEditValue(c.linkedinUrl ?? '')
              }}
            />
          </div>
        )
      },
    },
    {
      accessorKey: 'source',
      header: 'Quelle',
      cell: ({ row }) => (
        <StatusBadge
          status={BP_SOURCE_STATUS[row.original.source] ?? 'default'}
          label={BP_SOURCE_LABEL[row.original.source] ?? row.original.source}
        />
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {data?.tanssErrors?.length ? (
        <Card>
          <Card.Body>
            <div className="flex items-start gap-2 text-orange-600">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-body font-medium">TANSS-Personen konnten nicht geladen werden</p>
                <p className="text-small text-gray-600">{data.tanssErrors[0]}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">Ansprechpartner</h3>
          {data && <span className="text-small text-gray-500">({data.total} gesamt)</span>}
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading && <LoadingState label="Lade Ansprechpartner …" />}
          {isError && (
            <ErrorState
              title="Fehler beim Laden"
              description={(error as Error)?.message ?? 'Unbekannter Fehler'}
            />
          )}
          {!isLoading && !isError && (
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              pagination={{ pageSize: 20 }}
              emptyState={
                <EmptyState
                  title="Keine Ansprechpartner"
                  description="Für diesen Geschäftspartner sind keine Kontakte in SAP, TANSS oder lokal hinterlegt."
                />
              }
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Finanz-KPIs-Tab ──────────────────────────────────────────────────────────

interface OpenInvoice {
  DocEntry: number
  DocNum: number
  CardCode: string
  CardName: string
  DocTotal: number
  PaidToDate: number
  DocCurrency: string
  DocDate: string | null
  DocDueDate: string | null
  DocType: string
}

interface FinanzKpis {
  openInvoices: OpenInvoice[]
  openTotal: number
  openCount: number
  overdueTotal: number
  overdueCount: number
  aging: { bucket0to30: number; bucket31to60: number; bucket61to90: number; bucketOver90: number }
  dueIn7Days: number
  dueIn30Days: number
  volumeYtd: number
  revenueYtd: number  // legacy alias → volumeYtd
  invoiceCountYtd: number
  dso: number | null
  dsoRating: 'good' | 'fair' | 'poor' | null
  dunningBlock: boolean
  bpType: 'customer' | 'supplier' | 'prospect' | null
  tenantId: string | null
  sapUnavailable?: boolean
}

const OPEN_INVOICE_COLS: ColumnDef<OpenInvoice>[] = [
  {
    accessorKey: 'DocNum',
    header: 'Beleg-Nr.',
    cell: ({ row }) => row.original.DocNum,
  },
  {
    accessorKey: 'DocDate',
    header: 'Datum',
    cell: ({ row }) => row.original.DocDate ? new Date(row.original.DocDate).toLocaleDateString('de-DE') : '—',
  },
  {
    accessorKey: 'DocDueDate',
    header: 'Fälligkeit',
    cell: ({ row }) => {
      if (!row.original.DocDueDate) return '—'
      const due = new Date(row.original.DocDueDate)
      const isOverdue = due < new Date()
      return (
        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
          {due.toLocaleDateString('de-DE')}
        </span>
      )
    },
  },
  {
    accessorKey: 'DocTotal',
    header: 'Betrag',
    cell: ({ row }) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: row.original.DocCurrency ?? 'EUR' }).format(row.original.DocTotal),
  },
  {
    id: 'open',
    header: 'Offen',
    cell: ({ row }) => {
      const open = row.original.DocTotal - (row.original.PaidToDate ?? 0)
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: row.original.DocCurrency ?? 'EUR' }).format(open)
    },
  },
]

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <p className="text-small text-gray-500">{label}</p>
      <p className="text-h2 font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-small text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}

const DSO_RATING_CONFIG = {
  good: { label: 'Gut', color: 'text-green-700 bg-green-50 border-green-200' },
  fair: { label: 'Mittel', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
  poor: { label: 'Kritisch', color: 'text-red-700 bg-red-50 border-red-200' },
} as const

function DsoCard({
  dso,
  dsoRating,
  isSupplier,
}: {
  dso: number | null
  dsoRating: 'good' | 'fair' | 'poor' | null
  isSupplier: boolean
}) {
  const label = isSupplier ? 'DPO (Kreditorenlaufzeit)' : 'DSO (Debitorenlaufzeit)'
  const formula = 'Gewichtetes Ø-Alter offener Posten (Σ Betrag × Tage ÷ Σ Betrag)'
  const emptyHint = isSupplier ? 'Keine offenen Verbindlichkeiten' : 'Keine offenen Forderungen'
  const descriptions: Record<'good' | 'fair' | 'poor', string> = isSupplier
    ? { good: '≤ 30 Tage – wir zahlen zügig', fair: '31–45 Tage – üblicher Rahmen', poor: '> 45 Tage – Zahlungsziel prüfen' }
    : { good: '≤ 30 Tage – Kunde zahlt zügig', fair: '31–45 Tage – im üblichen Rahmen', poor: '> 45 Tage – Mahnwesen prüfen' }

  if (dso === null) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-small text-gray-500">{label}</p>
        <p className="text-h2 font-bold text-gray-400 mt-1">–</p>
        <p className="text-small text-gray-400 mt-0.5">{emptyHint}</p>
      </div>
    )
  }
  const cfg = dsoRating ? DSO_RATING_CONFIG[dsoRating] : null
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <p className="text-small text-gray-500">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className="text-h2 font-bold text-gray-900">{Math.round(dso)} Tage</p>
        {cfg && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.color}`}>
            {cfg.label}
          </span>
        )}
      </div>
      {dsoRating && <p className="text-small text-gray-500 mt-0.5">{descriptions[dsoRating]}</p>}
      <p className="text-xs text-gray-400 mt-2">{formula}</p>
    </div>
  )
}

interface AgingBuckets {
  bucket0to30: number
  bucket31to60: number
  bucket61to90: number
  bucketOver90: number
}

function AgingBar({ aging, fmt }: { aging: AgingBuckets; fmt: (v: number) => string }) {
  const total = aging.bucket0to30 + aging.bucket31to60 + aging.bucket61to90 + aging.bucketOver90
  if (total <= 0) return null
  const pct = (v: number) => `${((v / total) * 100).toFixed(1)}%`
  const segments = [
    { key: '0-30', label: '1–30 Tage', amount: aging.bucket0to30, color: 'bg-green-400' },
    { key: '31-60', label: '31–60 Tage', amount: aging.bucket31to60, color: 'bg-yellow-400' },
    { key: '61-90', label: '61–90 Tage', amount: aging.bucket61to90, color: 'bg-orange-400' },
    { key: '90+', label: '> 90 Tage', amount: aging.bucketOver90, color: 'bg-red-500' },
  ].filter((s) => s.amount > 0)
  return (
    <div className="space-y-2">
      <p className="text-small font-medium text-gray-700">Fälligkeitsstruktur überfälliger Posten</p>
      <div className="flex h-4 w-full overflow-hidden rounded-full bg-gray-100">
        {segments.map((s) => (
          <div key={s.key} className={`${s.color} h-full`} style={{ width: pct(s.amount) }} title={`${s.label}: ${fmt(s.amount)}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${s.color}`} />
            {s.label}: {fmt(s.amount)}
          </span>
        ))}
      </div>
    </div>
  )
}

function FinanzKpisTab({ bpId }: { bpId: string }) {
  const [selectedDoc, setSelectedDoc] = useState<CachedDocument | null>(null)
  const { data, isLoading, isError, error } = useQuery<FinanzKpis>({
    queryKey: ['bp-finance-kpis', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/finance-kpis`).then((r) => r.data),
  })

  function openInvoiceAsDoc(inv: OpenInvoice): void {
    if (!data?.tenantId) return
    const docType = data.bpType === 'supplier' ? 'purchase_invoice' : 'invoice'
    setSelectedDoc({
      id: `open-${inv.DocEntry}`,
      tenantId: data.tenantId,
      docType,
      sapDocEntry: inv.DocEntry,
      sapDocNum: inv.DocNum,
      cardCode: inv.CardCode,
      cardName: inv.CardName,
      docDate: inv.DocDate,
      docDueDate: inv.DocDueDate,
      docTotal: inv.DocTotal,
      docCurrency: inv.DocCurrency ?? 'EUR',
      docStatus: 'bost_Open',
      isClosed: false,
      isCancelled: false,
      comments: null,
      linesJson: null,
      fullDataJson: null,
      lastSyncedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      source: 'sap',
    })
  }

  const fmt = (v: number, currency = 'EUR') =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(v)

  return (
    <div className="space-y-4">
      {isLoading && <LoadingState label="Lade Finanz-KPIs …" />}
      {isError && (
        <ErrorState
          title="Fehler beim Laden"
          description={(error as Error)?.message ?? 'Unbekannter Fehler'}
        />
      )}
      {data && (
        <>
          {(() => {
            const isSupplier = data.bpType === 'supplier'
            const volume = data.volumeYtd ?? data.revenueYtd
            const openLabel = isSupplier ? 'Offene Verbindlichkeiten' : 'Offene Forderungen'
            const volumeLabel = isSupplier ? 'Einkauf YTD' : 'Umsatz YTD'
            const invoiceWord = isSupplier ? 'Eingangsrechnung' : 'Rechnung'
            const openCount = data.openCount ?? data.openInvoices.length
            return (
          <>
            {data.sapUnavailable && (
              <div className="flex items-start gap-3 rounded-lg border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                <span className="mt-0.5 text-orange-500">⚠</span>
                <span><strong>SAP nicht erreichbar</strong> – Daten werden aus dem Cache geladen. Offene Posten und Umsatz könnten veraltet sein.</span>
              </div>
            )}
            {!isSupplier && data.dunningBlock && (
              <div className="flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                <span className="mt-0.5 text-yellow-500">⚠</span>
                <span><strong>Mahnsperre aktiv</strong> – Kunde ist in SAP von Mahnläufen ausgenommen.</span>
              </div>
            )}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-small text-gray-500">{openLabel}</p>
              <p className="text-h2 font-bold text-gray-900 mt-1">{fmt(data.openTotal)}</p>
              <p className="text-small text-gray-500 mt-0.5">{openCount} offene {invoiceWord}{openCount !== 1 ? 'en' : ''}</p>
              {(data.overdueCount ?? 0) > 0 && (
                <span className="mt-1.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 border border-red-200">
                  {data.overdueCount} überfällig
                </span>
              )}
            </div>
            <KpiCard
              label={volumeLabel}
              value={fmt(volume)}
              sub={`${data.invoiceCountYtd} ${invoiceWord}${data.invoiceCountYtd !== 1 ? 'en' : ''} ${new Date().getFullYear()}`}
            />
            <DsoCard dso={data.dso} dsoRating={data.dsoRating} isSupplier={isSupplier} />
          </div>
          {(data.overdueTotal ?? 0) > 0 && (
            <AgingBar aging={data.aging} fmt={(v) => fmt(v)} />
          )}
          {isSupplier && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <span className="font-medium">Liquiditätsvorschau:</span>
              {' '}Fällig in 7 Tagen: <strong>{fmt(data.dueIn7Days ?? 0)}</strong>
              {' | '}Fällig in 30 Tagen: <strong>{fmt(data.dueIn30Days ?? 0)}</strong>
            </div>
          )}
          </>
            )
          })()}

          <Card>
            <Card.Header>
              <h3 className="text-h4 text-gray-900">
                {data.bpType === 'supplier' ? 'Offene Verbindlichkeiten' : 'Offene Posten'}
              </h3>
            </Card.Header>
            <Card.Body className="p-0">
              <DataTable
                columns={OPEN_INVOICE_COLS}
                data={data.openInvoices}
                pagination={{ pageSize: 20 }}
                onRowClick={data.tenantId ? openInvoiceAsDoc : undefined}
                emptyState={
                  <EmptyState
                    title="Keine offenen Posten"
                    description="Alle Rechnungen sind bezahlt oder kein SAP-Zugriff verfügbar."
                  />
                }
              />
            </Card.Body>
          </Card>
        </>
      )}
      <DocumentDetailModal
        doc={selectedDoc}
        open={!!selectedDoc}
        onOpenChange={(open) => { if (!open) setSelectedDoc(null) }}
        onConvert={() => {}}
      />
    </div>
  )
}

// ─── Aktivitaeten-Tab ──────────────────────────────────────────────────────────

interface BpNote { id: string; authorUserId: string | null; body: string; createdAt: string }
interface BpTask { id: string; title: string; dueAt: string | null; status: 'open' | 'done'; assigneeUserId: string | null; createdAt: string }

type BpActivityKind = 'task' | 'note'
type BpActivityItem =
  | { kind: 'task'; id: string; sortDate: string; data: BpTask }
  | { kind: 'note'; id: string; sortDate: string; data: BpNote }

function AktivitaetenTab({ bpId }: { bpId: string }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [filter, setFilter] = useState<BpActivityKind | 'all'>('all')
  const [showAddTask, setShowAddTask] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [noteBody, setNoteBody] = useState('')
  const [taskTitle, setTaskTitle] = useState('')
  const [taskDueAt, setTaskDueAt] = useState('')

  const notesQuery = useQuery<BpNote[]>({
    queryKey: ['bp-notes', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/notes`).then((r) => r.data),
  })
  const tasksQuery = useQuery<BpTask[]>({
    queryKey: ['bp-tasks', bpId],
    queryFn: () => apiClient.get(`/business-partners/${bpId}/tasks`).then((r) => r.data),
  })

  const addNote = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${bpId}/notes`, { body: noteBody }),
    onSuccess: () => {
      setNoteBody('')
      setShowAddNote(false)
      queryClient.invalidateQueries({ queryKey: ['bp-notes', bpId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })
  const deleteNote = useMutation({
    mutationFn: (noteId: string) => apiClient.delete(`/business-partners/${bpId}/notes/${noteId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bp-notes', bpId] }),
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })
  const addTask = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${bpId}/tasks`, { title: taskTitle, dueAt: taskDueAt || null }),
    onSuccess: () => {
      setTaskTitle('')
      setTaskDueAt('')
      setShowAddTask(false)
      queryClient.invalidateQueries({ queryKey: ['bp-tasks', bpId] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })
  const toggleTask = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      apiClient.patch(`/business-partners/${bpId}/tasks/${taskId}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bp-tasks', bpId] }),
  })
  const deleteTask = useMutation({
    mutationFn: (taskId: string) => apiClient.delete(`/business-partners/${bpId}/tasks/${taskId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bp-tasks', bpId] }),
  })

  const allItems: BpActivityItem[] = [
    ...(tasksQuery.data ?? []).map((t): BpActivityItem => ({
      kind: 'task', id: t.id, sortDate: t.dueAt ?? t.createdAt, data: t,
    })),
    ...(notesQuery.data ?? []).map((n): BpActivityItem => ({
      kind: 'note', id: n.id, sortDate: n.createdAt, data: n,
    })),
  ].sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime())

  const visible = filter === 'all' ? allItems : allItems.filter((i) => i.kind === filter)
  const openTaskCount = (tasksQuery.data ?? []).filter((t) => t.status === 'open').length
  const noteCount = (notesQuery.data ?? []).length
  const isLoading = notesQuery.isLoading || tasksQuery.isLoading
  const isError = notesQuery.isError || tasksQuery.isError
  const errorDetails = [
    notesQuery.error ? `Notizen: ${(notesQuery.error as Error).message}` : null,
    tasksQuery.error ? `Aufgaben: ${(tasksQuery.error as Error).message}` : null,
  ].filter(Boolean).join('\n')

  if (isError) {
    return (
      <ErrorState
        title="Aktivitäten konnten nicht geladen werden"
        details={errorDetails || undefined}
        onRetry={() => { void notesQuery.refetch(); void tasksQuery.refetch() }}
      />
    )
  }

  return (
    <div className="space-y-3">
      {/* Filter chips + Add buttons */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <FilterChip active={filter === 'all'} onRemove={undefined} onClick={() => setFilter('all')}>
            Alle {allItems.length > 0 && <span className="ml-1 text-xs opacity-70">{allItems.length}</span>}
          </FilterChip>
          <FilterChip active={filter === 'task'} onRemove={undefined} onClick={() => setFilter(filter === 'task' ? 'all' : 'task')}>
            <ListTodo className="w-3 h-3 mr-1 inline-block" />
            Aufgaben {openTaskCount > 0 && <span className="ml-1 text-xs opacity-70">{openTaskCount}</span>}
          </FilterChip>
          <FilterChip active={filter === 'note'} onRemove={undefined} onClick={() => setFilter(filter === 'note' ? 'all' : 'note')}>
            <StickyNote className="w-3 h-3 mr-1 inline-block" />
            Notizen {noteCount > 0 && <span className="ml-1 text-xs opacity-70">{noteCount}</span>}
          </FilterChip>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => { setShowAddTask((v) => !v); setShowAddNote(false) }}>
            + Aufgabe
          </Button>
          <Button variant="secondary" size="sm" onClick={() => { setShowAddNote((v) => !v); setShowAddTask(false) }}>
            + Notiz
          </Button>
        </div>
      </div>

      {/* Quick-add: Task */}
      {showAddTask && (
        <Card>
          <Card.Body>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                className="flex-1 min-w-48 border border-gray-300 rounded-md px-3 py-1.5 text-small focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Aufgabe …"
                value={taskTitle}
                autoFocus
                onChange={(e) => setTaskTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && taskTitle.trim()) addTask.mutate() }}
              />
              <input
                type="date"
                className="border border-gray-300 rounded-md px-3 py-1.5 text-small focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={taskDueAt}
                onChange={(e) => setTaskDueAt(e.target.value)}
              />
              <Button variant="primary" size="sm" onClick={() => addTask.mutate()} disabled={!taskTitle.trim() || addTask.isPending}>
                Hinzufügen
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Quick-add: Note */}
      {showAddNote && (
        <Card>
          <Card.Body>
            <div className="flex flex-col gap-2">
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-small focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                rows={3}
                placeholder="Notiz …"
                value={noteBody}
                autoFocus
                onChange={(e) => setNoteBody(e.target.value)}
              />
              <div className="flex justify-end">
                <Button variant="primary" size="sm" onClick={() => addNote.mutate()} disabled={!noteBody.trim() || addNote.isPending}>
                  Notiz speichern
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Unified timeline */}
      {isLoading ? (
        <LoadingState label="Lade Aktivitäten …" />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Clock className="w-8 h-8 text-gray-300" />}
          title={filter === 'all' ? 'Noch keine Aktivitäten' : 'Keine Einträge in dieser Auswahl'}
          description={filter === 'all' ? 'Aufgaben und Notizen erscheinen hier.' : undefined}
        />
      ) : (
        <div className="space-y-2">
          {visible.map((item) => {
            if (item.kind === 'task') {
              const task = item.data
              const overdue = task.dueAt && new Date(task.dueAt) < new Date() && task.status === 'open'
              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${task.status === 'done' ? 'border-gray-100 bg-gray-50' : 'border-gray-200 bg-white'}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={task.status === 'done'}
                      onChange={() => toggleTask.mutate({ taskId: task.id, status: task.status === 'done' ? 'open' : 'done' })}
                      className="w-4 h-4 rounded accent-brand-600 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-body ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <ListTodo className="w-3 h-3" /> Aufgabe
                      </span>
                      {task.dueAt && (
                        <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                          <Clock className="w-3 h-3" />
                          {new Date(task.dueAt).toLocaleDateString('de-DE')}
                          {overdue && ' · überfällig'}
                        </span>
                      )}
                    </div>
                  </div>
                  <IconButton
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    aria-label="Aufgabe löschen"
                    size="xs"
                    variant="ghost"
                    onClick={() => deleteTask.mutate(task.id)}
                  />
                </div>
              )
            }

            const note = item.data
            return (
              <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border border-yellow-100 bg-yellow-50">
                <StickyNote className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-body text-gray-800 whitespace-pre-wrap">{note.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(note.createdAt).toLocaleString('de-DE')}</p>
                </div>
                <IconButton
                  icon={<Trash2 className="w-3.5 h-3.5" />}
                  aria-label="Notiz löschen"
                  size="xs"
                  variant="ghost"
                  onClick={() => deleteNote.mutate(note.id)}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}


// ─── Seite ────────────────────────────────────────────────────────────────────

export default function GpDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: bp, isLoading, isError, error, refetch, isFetching } = useQuery<BusinessPartner>({
    queryKey: ['business-partner', id],
    queryFn: () => apiClient.get(`/business-partners/${id}`).then((r) => r.data),
    enabled: !!id,
  })
  const { toast } = useToast()
  const syncMutation = useMutation({
    mutationFn: () => apiClient.post(`/business-partners/${id}/sync`, {}),
    onSuccess: () => { toast({ tone: 'success', title: 'Sync erfolgreich' }); void refetch() },
    onError: (err: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serverMsg = (err as any)?.response?.data?.error as string | undefined
      toast({ tone: 'danger', title: 'Sync fehlgeschlagen', description: serverMsg ?? (err as Error).message })
    },
  })

  if (isLoading) return <LoadingState label="Lade Geschäftspartner …" />

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/gp')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Button>
        <ErrorState
          title="Fehler beim Laden"
          description={(error as Error)?.message ?? 'Unbekannter Fehler'}
        />
      </div>
    )
  }

  if (!bp) return null

  return (
    <div className="space-y-4">
      <PageHeader
        title={bp.name}
        description={
          <span className="flex items-center gap-2">
            <StatusBadge status={TYPE_STATUS[bp.bpType] ?? 'default'} label={TYPE_LABEL[bp.bpType] ?? bp.bpType} />
            {bp.sapCardCode && (
              <span className="text-small text-gray-500">SAP: {bp.sapCardCode}</span>
            )}
            {!bp.isActive && (
              <StatusBadge status="inactive" label="Inaktiv" />
            )}
          </span>
        }
        breadcrumbs={[
          { label: 'Geschäftspartner', to: '/gp' },
          { label: bp.name },
        ]}
        actions={
          <>
            <Button
              variant="ghost"
             
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {syncMutation.isPending ? 'Synchronisiere …' : 'Sync aus SAP'}
            </Button>
            <Button variant="secondary" disabled>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          </>
        }
      />

      <Tabs defaultValue="stammdaten">
        <TabsList>
          <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
          <TabsTrigger value="dokumente">Dokumente</TabsTrigger>
          <TabsTrigger value="vertraege">Verträge</TabsTrigger>
          <TabsTrigger value="ansprechpartner">Ansprechpartner</TabsTrigger>
          <TabsTrigger value="finanzkpis">Finanz-KPIs</TabsTrigger>
          <TabsTrigger value="aktivitaeten">Aktivitäten</TabsTrigger>
          <TabsTrigger value="datenpflege">Datenpflege</TabsTrigger>
          <TabsTrigger value="handelsregister">Handelsregister</TabsTrigger>
        </TabsList>

        <TabsContent value="stammdaten" className="mt-4">
          <StammdatenTab bp={bp} onRefresh={() => refetch()} />
        </TabsContent>

        <TabsContent value="dokumente" className="mt-4">
          <DokumenteTab bpId={bp.id} />
        </TabsContent>

        <TabsContent value="vertraege" className="mt-4">
          <VertraegeTab bpId={bp.id} />
        </TabsContent>

        <TabsContent value="ansprechpartner" className="mt-4">
          <AnsprechpartnerTab bpId={bp.id} />
        </TabsContent>

        <TabsContent value="finanzkpis" className="mt-4">
          <FinanzKpisTab bpId={bp.id} />
        </TabsContent>

        <TabsContent value="aktivitaeten" className="mt-4">
          <AktivitaetenTab bpId={bp.id} />
        </TabsContent>

        <TabsContent value="datenpflege" className="mt-4">
          <DatepflegeTab bp={bp} onRefresh={() => refetch()} />
        </TabsContent>

        <TabsContent value="handelsregister" className="mt-4">
          <HandelsregisterTab
            bpId={bp.id}
            bpName={bp.name}
            sapRegistrationNumber={bp.sapRawJson?.['RegistrationNumber'] ? String(bp.sapRawJson['RegistrationNumber']) : null}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
